import { useCallback, useEffect, useRef, useState } from "react";
import type { ViewInstance } from "react-native";

/**
 * Measures the rendered width of a wrapper element on mount and on resize,
 * via `clientWidth` + `ResizeObserver` (web-only — this app runs on
 * react-native-web, and `View`'s ref forwards to the underlying DOM node
 * there). Returns `null` until the first measurement lands, so charts can
 * hold off drawing an SVG until they know their real pixel width instead of
 * authoring a fixed `viewBox` that then gets scaled — see
 * `evolutionChart.tsx` and the handoff's "Notas de implementación".
 *
 * `ResizeObserver` is guarded because this hook is shared code that could in
 * principle run outside a DOM environment (e.g. under the RN-free jest
 * config for `@repo/components`).
 */
export const useMeasuredWidth = (): [
  (node: ViewInstance | null) => void,
  number | null,
] => {
  const [width, setWidth] = useState<number | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);

  const measure = useCallback((node: ViewInstance) => {
    const element = node as unknown as HTMLElement;
    if (element && typeof element.clientWidth === "number") {
      setWidth(element.clientWidth);
    }
  }, []);

  const ref = useCallback(
    (node: ViewInstance | null) => {
      observerRef.current?.disconnect();
      observerRef.current = null;

      if (!node) return;

      measure(node);

      if (typeof ResizeObserver !== "undefined") {
        const element = node as unknown as HTMLElement;
        const observer = new ResizeObserver(() => measure(node));
        observer.observe(element);
        observerRef.current = observer;
      }
    },
    [measure],
  );

  useEffect(() => {
    return () => observerRef.current?.disconnect();
  }, []);

  return [ref, width];
};
